'use client';

import React from 'react';

import { ErrorFallback } from '@core/Components/ErrorFallback/ErrorFallback';
import type { Locale } from '@core/Entities/Locale/Locale.entity';

type LandingPageErrorBoundaryProps = {
  children: React.ReactNode;
  componentType?: string;
  locale: Locale;
  slug: string;
};

type LandingPageErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
};

export class LandingPageErrorBoundary extends React.Component<
  LandingPageErrorBoundaryProps,
  LandingPageErrorBoundaryState
> {
  constructor(props: LandingPageErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): LandingPageErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const { componentType, locale, slug } = this.props;

    // Enhanced error logging with context
    console.error('Landing Page Component Error:', {
      componentType,
      locale,
      slug,
      error: error.message,
      stack: error.stack,
      errorInfo,
      timestamp: new Date().toISOString(),
    });

    // Log to any external error tracking service if available
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'exception', {
        description: `Landing Page Error: ${componentType} - ${error.message}`,
        fatal: false,
        custom_map: {
          locale,
          slug,
          componentType,
        },
      });
    }
  }

  render(): React.ReactNode {
    const { hasError, error } = this.state;
    const { children, componentType, locale, slug } = this.props;

    if (hasError && error) {
      return (
        <ErrorFallback
          fallback={(error: Error) => (
            <div
              style={{
                padding: '20px',
                margin: '10px 0',
                border: '1px solid #ff6b6b',
                borderRadius: '4px',
                backgroundColor: '#fff5f5',
                color: '#c92a2a',
              }}
            >
              <h3>Component Error</h3>
              <p>
                <strong>Component:</strong> {componentType || 'Unknown'}
              </p>
              <p>
                <strong>Page:</strong> {slug}
              </p>
              <p>
                <strong>Locale:</strong> {locale}
              </p>
              <p>
                <strong>Error:</strong> {error.message}
              </p>
              {process.env.NODE_ENV === 'development' && (
                <details>
                  <summary>Stack Trace</summary>
                  <pre style={{ fontSize: '12px', overflow: 'auto' }}>{error.stack}</pre>
                </details>
              )}
            </div>
          )}
        >
          {children}
        </ErrorFallback>
      );
    }

    return children;
  }
}
